// cw: Individual TY page — shows user's own pending requests + historical given/received
import React, { useEffect, useState } from 'react';
import ApiClient from '../../methods/api/apiClient';
import { useSelector } from 'react-redux';
import { FaHandshake, FaTrophy, FaInbox, FaHistory, FaLock, FaStar } from 'react-icons/fa';
import loader from '../../methods/loader';
import Layout from '../../components/global/layout';
import Modal from '../../components/common/Modal';
import { toast } from 'react-toastify';

const TestimonialsIndividual = () => {
    const user = useSelector((state: any) => state.user);
    const [stats, setStats] = useState<any>(null);
    const [myRequests, setMyRequests] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [viewMode, setViewMode] = useState<'pending' | 'history'>('pending');

    // cw: Give-a-testimonial form state (recipient + type + note + AI draft)
    const [members, setMembers] = useState<any[]>([]);
    const [giveRecipientId, setGiveRecipientId] = useState('');
    const [giveType, setGiveType] = useState('');
    const [giveNote, setGiveNote] = useState('');
    const [sharedGroups, setSharedGroups] = useState<any[]>([]);
    const [drafting, setDrafting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Premium "write in my own voice" upsell — opens the same paywall popup as the EventDetail TY writer.
    const [showOwnVoice, setShowOwnVoice] = useState(false);
    const handleSubscribeOwnVoice = () => { toast('Payment setup coming soon.'); };

    const displayLabel = stats?.label || user?.testimonialLabel || 'Thank Yous';
    const singularLabel = displayLabel.replace(/s$/, '');

    const getDisplayName = (member: any) =>
        member?.fullName ||
        `${member?.firstName || ''} ${member?.lastName || ''}`.trim() ||
        member?.email ||
        'Member';

    // Load shared groups when a recipient is picked — we need a groupId for submit.
    const handleRecipientChange = async (rid: string) => {
        setGiveRecipientId(rid);
        setSharedGroups([]);
        if (rid) {
            const res = await ApiClient.get('api/testimonial/shared-groups', { receiverId: rid });
            if (res.success && res.data) setSharedGroups(res.data);
        }
    };

    // AI draft: expand whatever the user typed into a fuller note. Note must be non-empty
    // (the button enforces that) and a recipient must be selected (backend recipe needs it).
    const draftNote = async () => {
        if (!giveRecipientId) { toast.error("Select who you're thanking first"); return; }
        if (!giveNote.trim()) return;
        setDrafting(true);
        try {
            // hideError=true so ApiClient doesn't also toast — we show exactly one message here.
            const res: any = await ApiClient.post('api/testimonial/draft', { receivedById: giveRecipientId, context: giveNote || '' }, '', true);
            const body = res?.data && typeof res.data === 'object' ? res.data : res;
            if (res?.success && body.draft) setGiveNote(body.draft);
            else toast.error(res?.message || 'Could not draft');
        } catch (e: any) {
            toast.error(e?.response?.data?.message || 'Could not draft');
        }
        setDrafting(false);
    };

    const submitGive = async () => {
        if (!giveRecipientId) { toast.error('Please select a member'); return; }
        const groupIds = sharedGroups.map((g: any) => g._id);
        if (groupIds.length === 0) {
            toast.error('You and this member must share an active group');
            return;
        }
        setSubmitting(true);
        try {
            const res = await ApiClient.post('api/testimonial/submit', {
                receivedById: giveRecipientId,
                groupId: groupIds[0],
                groupIds: groupIds,
                thankYouType: giveType || null,
                note: giveNote || null
            });
            if (res.success) {
                toast.success(`${singularLabel} request submitted`);
                setGiveRecipientId('');
                setGiveType('');
                setGiveNote('');
                setSharedGroups([]);
                getData();
            } else {
                toast.error(res.message || 'Failed to submit');
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to submit');
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        loader(true);
        try {
            const [statsRes, reqRes, membersRes] = await Promise.all([
                ApiClient.get('api/testimonial/stats'),
                ApiClient.get('api/testimonial/my-requests'),
                ApiClient.get('api/testimonial/members')
            ]);

            if (statsRes.success && statsRes.data) {
                setStats(statsRes.data);
            }
            if (reqRes.success) {
                setMyRequests(reqRes.data || []);
            }
            if (membersRes.success) {
                setMembers(membersRes.data || []);
            }
        } catch (error: any) {
            console.error('Error loading data:', error);
        } finally {
            loader(false);
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <Layout>
                <div className='mb-6'>
                    <h3 className="text-2xl font-semibold text-[#111827]">
                        {user?.testimonialLabel || 'Thank Yous'} Individual
                    </h3>
                    <p className="text-sm font-normal text-[#75757A]">Loading...</p>
                </div>
                <div className="text-center py-8">
                    <img src="/assets/img/loader.gif" className="pageLoader" alt="Loading..." />
                </div>
            </Layout>
        );
    }

    const pendingRequests = myRequests.filter(r => r.status === 'submitted' || r.status === 'deferred');
    const decidedRequests = myRequests.filter(r => r.status !== 'submitted' && r.status !== 'deferred');

    return (
        <Layout>
            <div className="pprofile1">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h3 className="text-2xl font-semibold text-[#111827]">
                            {displayLabel} Individual
                        </h3>
                        <p className="text-sm font-normal text-[#75757A]">
                            Your personal {displayLabel.toLowerCase()} — pending requests and history
                        </p>
                    </div>
                </div>

                {/* Give a {label} — recipient, type, then note (moved lower) with AI draft */}
                <div className="bg-white rounded-lg p-6 shadow-sm border mb-8">
                    <div className="mb-4">
                        <h4 className="text-lg font-semibold text-[#111827]">Give a {singularLabel}</h4>
                        <p className="text-sm text-gray-500">
                            Recognize a fellow group member. Your group facilitator will review it.
                        </p>
                    </div>
                    <div className="flex flex-col gap-4">
                        {/* Recipient */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Who are you thanking?</label>
                            <select
                                className="w-full border rounded-lg p-2 text-sm"
                                value={giveRecipientId}
                                onChange={(e) => handleRecipientChange(e.target.value)}
                            >
                                <option value="">Select member...</option>
                                {members
                                    .slice()
                                    .sort((a: any, b: any) =>
                                        getDisplayName(a).toLowerCase().localeCompare(getDisplayName(b).toLowerCase())
                                    )
                                    .map((m: any) => (
                                        <option key={m._id} value={m._id}>{getDisplayName(m)}</option>
                                    ))}
                            </select>
                            {members.length === 0 && (
                                <p className="text-xs text-gray-400 mt-1">No group members available.</p>
                            )}
                        </div>

                        {/* Type */}
                        <div>
                            <label className="block text-sm font-medium mb-1">Type (optional)</label>
                            <select
                                className="w-full border rounded-lg p-2 text-sm"
                                value={giveType}
                                onChange={(e) => setGiveType(e.target.value)}
                            >
                                <option value="">Select type...</option>
                                <option value="referral">Referral</option>
                                <option value="introduction">Introduction</option>
                                <option value="business_outcome">Business Outcome</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        {/* Note (moved lower, below recipient/type) + AI draft */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium">Note (optional)</label>
                                {giveNote && (
                                    <button
                                        type="button"
                                        onClick={() => setGiveNote('')}
                                        className="text-xs text-red-500 hover:text-red-700"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 mb-1">
                                Share what you are thanking them for. Optionally click Draft with AI to enhance the entry.
                            </p>
                            <textarea
                                className="w-full border rounded-lg p-2 text-sm"
                                value={giveNote}
                                onChange={(e) => setGiveNote(e.target.value)}
                                placeholder="Share what you are thanking them for..."
                                rows={giveNote.length > 120 ? 8 : 3}
                            />
                        </div>

                        {/* Actions — right-aligned. Submit (always bright green) on the left, Draft with AI
                            to its right, with the "Write in your own words" upsell under it. */}
                        <div className="flex items-start justify-end gap-3">
                            <button
                                disabled={submitting}
                                className="bg-[#22C55E] text-white px-4 py-2 rounded-lg hover:bg-green-600 text-sm font-medium"
                                onClick={submitGive}
                            >
                                {submitting ? 'Submitting...' : `Submit ${singularLabel}`}
                            </button>
                            <div className="flex flex-col items-start">
                                <button
                                    type="button"
                                    disabled={!giveNote.trim() || drafting}
                                    onClick={draftNote}
                                    className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {drafting ? 'Drafting…' : 'Draft with AI'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowOwnVoice(true)}
                                    className="mt-1 inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium text-white bg-orange-300 hover:bg-orange-400"
                                >
                                    <FaStar className="text-[10px]" /> Write in your own words
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Given</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {stats?.givenCount || 0}
                                </h3>
                            </div>
                            <FaHandshake className="text-4xl text-orange-500 opacity-50" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Received</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {stats?.receivedCount || 0}
                                </h3>
                            </div>
                            <FaTrophy className="text-4xl text-green-500 opacity-50" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Pending Requests</p>
                                <h3 className="text-3xl font-bold text-[#111827]">
                                    {pendingRequests.length}
                                </h3>
                            </div>
                            <FaInbox className="text-4xl text-blue-500 opacity-50" />
                        </div>
                    </div>
                </div>

                {/* View Toggle */}
                <div className="flex gap-2 mb-4">
                    <button
                        onClick={() => setViewMode('pending')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                            viewMode === 'pending'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaInbox /> Pending Requests {pendingRequests.length > 0 && `(${pendingRequests.length})`}
                    </button>
                    <button
                        onClick={() => setViewMode('history')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                            viewMode === 'history'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                        <FaHistory /> History
                    </button>
                </div>

                {/* Pending Requests */}
                {viewMode === 'pending' && (
                    <div className="bg-white rounded-lg shadow-sm border">
                        <div className="p-4 border-b">
                            <h4 className="text-lg font-semibold">My Pending {displayLabel} Requests</h4>
                            <p className="text-sm text-gray-500">
                                {displayLabel} requests you've submitted that are awaiting review
                            </p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">To</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Note</th>
                                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingRequests.map((req: any) => (
                                        <tr key={req._id} className="border-t hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm">
                                                {req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{req.receiverName || '-'}</td>
                                            <td className="px-4 py-3 text-sm capitalize">{req.thankYouType ? req.thankYouType.replace(/_/g, ' ') : '-'}</td>
                                            <td className="px-4 py-3 text-sm max-w-[200px] truncate" title={req.note}>
                                                {req.note || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    req.status === 'deferred'
                                                        ? 'bg-yellow-100 text-yellow-700'
                                                        : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    {req.status === 'deferred' ? 'Deferred' : 'Submitted'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {pendingRequests.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                                No pending requests
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Also show decided requests below */}
                        {decidedRequests.length > 0 && (
                            <>
                                <div className="p-4 border-t border-b bg-gray-50">
                                    <h5 className="text-md font-semibold text-gray-700">Past Request Decisions</h5>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">To</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Note</th>
                                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {decidedRequests.map((req: any) => {
                                                const statusColors: any = {
                                                    accepted: 'bg-green-100 text-green-700',
                                                    declined: 'bg-red-100 text-red-700',
                                                    expired: 'bg-gray-100 text-gray-500',
                                                };
                                                return (
                                                    <tr key={req._id} className="border-t hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm">
                                                            {req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">{req.receiverName || '-'}</td>
                                                        <td className="px-4 py-3 text-sm capitalize">{req.thankYouType ? req.thankYouType.replace(/_/g, ' ') : '-'}</td>
                                                        <td className="px-4 py-3 text-sm max-w-[200px] truncate" title={req.note}>
                                                            {req.note || '-'}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[req.status] || 'bg-gray-100 text-gray-500'}`}>
                                                                {req.status?.charAt(0).toUpperCase() + req.status?.slice(1)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* History View — Given and Received */}
                {viewMode === 'history' && (
                    <div className="space-y-6">
                        {/* Given */}
                        <div className="bg-white rounded-lg shadow-sm border">
                            <div className="p-4 border-b">
                                <h4 className="text-lg font-semibold">{displayLabel} I've Given ({stats?.givenCount || 0})</h4>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">To</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(stats?.givenTo || []).map((item: any, i: number) => (
                                            <tr key={i} className="border-t hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm">
                                                    {item.testimonialDate ? new Date(item.testimonialDate).toLocaleDateString() : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {item.fullName || item.email || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                        {(!stats?.givenTo || stats.givenTo.length === 0) && (
                                            <tr>
                                                <td colSpan={2} className="px-4 py-8 text-center text-gray-500">
                                                    No {displayLabel.toLowerCase()} given yet
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Received */}
                        <div className="bg-white rounded-lg shadow-sm border">
                            <div className="p-4 border-b">
                                <h4 className="text-lg font-semibold">{displayLabel} I've Received ({stats?.receivedCount || 0})</h4>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">From</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(stats?.receivedFrom || []).map((item: any, i: number) => (
                                            <tr key={i} className="border-t hover:bg-gray-50">
                                                <td className="px-4 py-3 text-sm">
                                                    {item.testimonialDate ? new Date(item.testimonialDate).toLocaleDateString() : '-'}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {item.fullName || item.email || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                        {(!stats?.receivedFrom || stats.receivedFrom.length === 0) && (
                                            <tr>
                                                <td colSpan={2} className="px-4 py-8 text-center text-gray-500">
                                                    No {displayLabel.toLowerCase()} received yet
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showOwnVoice ? (
                <Modal
                    title="Write in Your Own Voice"
                    body={
                        <div className="flex flex-col gap-4">
                            <p className="text-sm text-gray-600">
                                Have your {displayLabel.toLowerCase()} written in <strong>your own voice</strong>. Upload up to 2 writing
                                samples and/or paste some of your own writing. The AI uses these as a style reference.
                            </p>
                            <div className="relative">
                                {/* Paywall overlay — boxes are visible but locked until subscribed. */}
                                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/40 rounded-lg text-center px-4">
                                    <FaLock className="text-2xl text-gray-700" />
                                    <p className="text-sm font-semibold text-gray-900">Premium feature — $2/month</p>
                                    <p className="text-xs text-gray-700">Subscribe to upload samples and write in your own voice.</p>
                                    <button
                                        type="button"
                                        onClick={handleSubscribeOwnVoice}
                                        className="mt-1 bg-[#EF7A2B] text-white text-sm px-4 py-2 rounded hover:bg-orange-600 shadow"
                                    >
                                        Subscribe – $2/month
                                    </button>
                                </div>
                                <div className="pointer-events-none select-none space-y-3 p-1">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-800 mb-1">Writing samples (up to 2 files)</label>
                                        <input type="file" multiple disabled className="text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-800 mb-1">Or paste your writing</label>
                                        <textarea
                                            disabled
                                            rows={5}
                                            maxLength={5000}
                                            placeholder="Paste a few paragraphs of your own writing…"
                                            className="w-full border rounded-lg p-2 text-sm"
                                        />
                                        <p className="text-xs text-gray-400 mt-1">Up to 5,000 characters.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    result={() => setShowOwnVoice(false)}
                />
            ) : null}
        </Layout>
    );
};

export default TestimonialsIndividual;
